import type { ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import { avatarInitials, avatarTone, avatarVariants } from '../avatar/avatar.tsx';

const organisationLogoVariants = cva('flex shrink-0 overflow-hidden rounded-md', {
  variants: {
    size: {
      sm: 'size-8 text-sm',
      md: 'size-11.5 text-md',
      xl: 'size-32 text-xl',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type OrganisationLogoVariants = VariantProps<typeof organisationLogoVariants>;

export interface OrganisationLogoProps
  extends
    Omit<ComponentPropsWithRef<'span'>, 'style' | 'children'>,
    OrganisationLogoVariants {
  /* The organisation's name, abbreviated when there is no logo. */
  name: string;
  /* The logo URL; versioned, so the browser fetches it once. */
  src?: string | null;
}

/**
 * An organisation, as a square: its logo when it has one, its initials when it
 * does not. Logos are drawn for a white page, so they sit on white in either
 * theme, uncropped.
 *
 * `aria-hidden`: the name always stands beside it.
 */
export function OrganisationLogo({
  className,
  name,
  src,
  size,
  ...props
}: OrganisationLogoProps) {
  return (
    <span
      data-slot='organisation-logo'
      aria-hidden='true'
      className={cn(
        organisationLogoVariants({ size }),
        /* The avatar's tone and lettering, in this component's square and size. */
        src
          ? 'border border-line bg-white'
          : [avatarVariants({ tone: avatarTone(name), size: null }), 'rounded-md'],
        className,
      )}
      {...props}
    >
      {src ? (
        <img src={src} alt='' className='size-full object-contain' />
      ) : (
        avatarInitials(name)
      )}
    </span>
  );
}

export { organisationLogoVariants };
